import CloseIcon from "@pages/profile-v2/assets/icons/CloseIcon.jsx";
import PropTypes from "prop-types";
import React, { memo, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { PROFILE_NAV_ITEMS, WALLET_NAV_ITEMS } from "../../constants";
import { BettoPageTitle } from "../../shared/elements";
import { BettoProfileNav } from "../BettoProfileNav";
import { BettoWalletTabs } from "../BettoWalletTabs";
import {
	DesktopNav,
	MobileNavSelect,
	ShellBackButton,
	ShellBody,
	ShellCloseLink,
	ShellContentColumn,
	ShellHeader,
	ShellHeaderLeft,
	ShellNavColumn,
	ShellRoot,
} from "./elements";

const getPageTitle = (path, translate) => {
	if (path === "/profile/settings") return translate("betto.profile.globalSettings");
	if (path === "/profile/deposit") return translate("betto.profile.deposit");
	if (path === "/profile/withdraw") return translate("betto.profile.withdraw");
	if (path === "/profile/promo-code") return translate("Promo Code");
	const navItem = [...PROFILE_NAV_ITEMS, ...WALLET_NAV_ITEMS].find((item) => item.link === path);
	if (navItem?.labelKey) return translate(navItem.labelKey);
	if (navItem?.label) return translate(navItem.label);
	return translate("Profile");
};

export const BettoProfileShell = memo(({ children, translate, currentPath, history }) => {
	const isPromoCodePage = currentPath === "/profile/promo-code";
	const isWalletPage =
		!isPromoCodePage &&
		(currentPath === "/profile/deposit" || currentPath === "/profile/withdraw");

	const pageTitle = useMemo(() => getPageTitle(currentPath, translate), [currentPath, translate]);

	const navItems = useMemo(
		() => (isWalletPage ? WALLET_NAV_ITEMS : PROFILE_NAV_ITEMS),
		[isWalletPage]
	);

	const handleMobileNavChange = useCallback(
		(event) => {
			history.push(event.target.value);
		},
		[history]
	);

	const handleBackToProfile = useCallback(() => {
		history.push("/profile/settings");
	}, [history]);

	return (
		<ShellRoot>
			<ShellHeader>
				<ShellHeaderLeft>
					{isPromoCodePage && (
						<ShellBackButton
							type="button"
							data-testid="button-profile-promo-back"
							onClick={handleBackToProfile}
							aria-label={translate("BACK")}>
							<svg
								viewBox="0 0 20 20"
								fill="none"
								aria-hidden="true">
								<path
									d="M12.5 15L7.5 10L12.5 5"
									stroke="currentColor"
									strokeWidth="1.75"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
							<span>{translate("BACK")}</span>
						</ShellBackButton>
					)}
					<BettoPageTitle>{pageTitle}</BettoPageTitle>
				</ShellHeaderLeft>
				<Link
					to="/"
					data-testid="button-profile-close">
					<ShellCloseLink as="span">
						<CloseIcon />
					</ShellCloseLink>
				</Link>
			</ShellHeader>

			<ShellBody>
				<ShellNavColumn>
					{!isWalletPage && (
						<MobileNavSelect
							value={currentPath}
							onChange={handleMobileNavChange}>
							{navItems.map((item) => (
								<option
									key={item.key}
									value={item.link}>
									{item.labelKey
										? translate(item.labelKey)
										: translate(item.label)}
								</option>
							))}
						</MobileNavSelect>
					)}
					<DesktopNav>
						<BettoProfileNav
							translate={translate}
							isWalletPage={isWalletPage}
							currentPath={currentPath}
						/>
					</DesktopNav>
				</ShellNavColumn>
				<ShellContentColumn>
					{isWalletPage && (
						<BettoWalletTabs
							translate={translate}
							currentPath={currentPath}
							history={history}
						/>
					)}
					{children}
				</ShellContentColumn>
			</ShellBody>
		</ShellRoot>
	);
});

BettoProfileShell.displayName = "BettoProfileShell";

BettoProfileShell.propTypes = {
	children: PropTypes.node,
	translate: PropTypes.func.isRequired,
	currentPath: PropTypes.string,
	history: PropTypes.object,
};
